import { useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import useStyles from "./styles";
import statusVersao from "../statusVersao";
import DatagridComponent from "../../../components/datagridComponent";
import ListaClientesDoItem from "../listaClientesDoItem";
import PainelSaudeColeta from "../painelSaudeColeta";
import EstadoVazio from "../estadoVazio";
import ModalAtualizarTodos from "../modalAtualizarTodos";

// Opcoes fixas do filtro de status - mesmos 6 literais que o backend devolve
const OPCOES_STATUS = [
  "Atualizado",
  "Desatualizado",
  "À frente do padrão",
  "Só no cliente",
  "Ausente no cliente",
  "Sem versão",
];

export default function AbaTemplates({
  templates,
  saudeColeta,
  loading,
  linhaSelecionada,
  setLinhaSelecionada,
  filtroCliente,
  setFiltroCliente,
  filtroStatus,
  setFiltroStatus,
  onAtualizarCache,
  coletando,
  onTemplateAtualizado,
}) {
  const [modalAtualizarTodosAberto, setModalAtualizarTodosAberto] =
    useState(false);
  const theme = useTheme();
  const styles = useStyles(theme);
  const cores = statusVersao(theme);

  // Limpa os dois campos de filtro de uma vez (acao do estado vazio "filtro sem resultado")
  const limparFiltro = () => {
    setFiltroCliente?.("");
    setFiltroStatus?.("");
  };

  const listaTemplates = templates || [];
  const listaSaude = saudeColeta || [];
  const filtroAtivo = Boolean(filtroCliente) || Boolean(filtroStatus);
  // Soma de TODOS os templates desatualizados, nao so os filtrados na tela - o botao "Atualizar
  // todos" afeta o sistema inteiro, o filtro de busca/status e so visual
  const totalDesatualizados = listaTemplates.reduce(
    (soma, template) => soma + (template?.desatualizado || 0),
    0,
  );

  // Filtragem em memoria: texto por nome/codigo da linha e presenca de algum cliente com o status escolhido
  const templatesFiltrados = listaTemplates.filter((template) => {
    const termo = filtroCliente?.toLowerCase()?.trim();
    const combinaTexto =
      !termo ||
      template?.nome?.toLowerCase()?.includes(termo) ||
      template?.codigo?.toLowerCase()?.includes(termo);
    const combinaStatus =
      !filtroStatus ||
      template?.clientes?.some((cliente) => cliente?.status === filtroStatus);
    return combinaTexto && combinaStatus;
  });

  const rows = templatesFiltrados.map((template) => ({
    ...template,
    id: template?.chave,
  }));

  const columns = [
    { field: "nome", headerName: "Nome", flex: 1, minWidth: 160 },
    {
      field: "versaoReferencia",
      headerName: "Versão referência",
      width: 170,
      // Linha "so no cliente": chave sem linha formal na referencia central, mostra chip em vez de celula vazia
      // (existeNaReferencia === false e o unico sinal confiavel - versaoReferencia tambem fica null
      // quando a linha existe na referencia mas a descricao nao tem versao parseavel)
      renderCell: (params) => {
        if (params?.row?.existeNaReferencia === false) {
          return (
            <Chip
              size="small"
              label="Só no cliente"
              sx={{
                ...styles.chip,
                backgroundColor: cores["Só no cliente"]?.background,
                color: cores["Só no cliente"]?.color,
              }}
            />
          );
        }
        return params?.row?.versaoReferencia;
      },
    },
    { field: "ok", headerName: "Clientes OK", width: 130 },
    { field: "desatualizado", headerName: "Desatualizados", width: 150 },
    { field: "ausente", headerName: "Sem o template", width: 150 },
  ];

  // Enquanto carrega, skeleton no formato do grid - nunca spinner solto no meio do conteudo
  if (loading) {
    return (
      <Box sx={styles.skeletonContainer}>
        <Skeleton
          variant="rectangular"
          height={40}
          sx={styles.skeletonHeader}
        />
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={32}
            sx={styles.skeletonRow}
          />
        ))}
      </Box>
    );
  }

  // Nenhum cliente com Zabbix cadastrado ainda
  if (listaTemplates.length === 0 && listaSaude.length === 0) {
    return <EstadoVazio tipo="sem-clientes" />;
  }

  // Clientes cadastrados, mas nenhuma coleta rodou ainda para nenhum deles
  if (
    listaTemplates.length === 0 &&
    listaSaude.length > 0 &&
    !listaSaude.some((cliente) => cliente?.status !== null)
  ) {
    return (
      <EstadoVazio
        tipo="sem-coleta"
        onAction={onAtualizarCache}
        disabled={coletando}
      />
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.barraFiltro}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nome ou código do template"
          value={filtroCliente || ""}
          onChange={(event) => setFiltroCliente?.(event.target.value)}
          sx={styles.buscaField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filtroCliente ? (
              <InputAdornment position="end">
                <Clear
                  fontSize="small"
                  sx={styles.limparBusca}
                  onClick={() => setFiltroCliente?.("")}
                />
              </InputAdornment>
            ) : null,
          }}
        />
        <FormControl size="small" sx={styles.statusField}>
          <Select
            value={filtroStatus || ""}
            displayEmpty
            onChange={(event) => setFiltroStatus?.(event.target.value)}
          >
            <MenuItem value="">Todos os status</MenuItem>
            {OPCOES_STATUS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip
          title={
            totalDesatualizados === 0
              ? "Nenhum template desatualizado no momento"
              : ""
          }
          disableHoverListener={totalDesatualizados > 0}
        >
          <span>
            <Button
              variant="contained"
              sx={styles.botaoAtualizarTodos}
              disabled={totalDesatualizados === 0}
              onClick={() => setModalAtualizarTodosAberto(true)}
            >
              Atualizar todos
            </Button>
          </span>
        </Tooltip>
      </Box>
      {templatesFiltrados.length === 0 && filtroAtivo ? (
        <EstadoVazio tipo="filtro-sem-resultado" onAction={limparFiltro} />
      ) : (
        // Paper garante o fundo/contraste corretos do grid nos dois modos, mesmo padrao ja usado em Clientes/Staff/Home
        <Paper
          sx={styles.paper}
          elevation={theme.palette.mode === "dark" ? 0 : 3}
        >
          <Box sx={styles.gridContainer}>
            <DatagridComponent
              rows={rows}
              columns={columns}
              setSelectedRow={setLinhaSelecionada}
              sxDatagrid={{
                height: "100%",
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundImage: theme.palette.gradient.buttonSelected,
                  "& .MuiDataGrid-cell": {
                    color: theme.palette.white.main,
                  },
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundImage: `linear-gradient(
                  90deg,
                  ${alpha(theme.palette.buttonHoverGradient.dark, 0.5)} 0%,
                  ${alpha(theme.palette.buttonHoverGradient.light, 0.5)} 100%
                )`,
                },
              }}
              options={{
                disableRowSelectionOnClick: false,
              }}
            />
          </Box>
        </Paper>
      )}
      <ListaClientesDoItem
        item={linhaSelecionada}
        onTemplateAtualizado={onTemplateAtualizado}
      />
      <PainelSaudeColeta saudeColeta={listaSaude} />
      <ModalAtualizarTodos
        open={modalAtualizarTodosAberto}
        onClose={() => setModalAtualizarTodosAberto(false)}
        templates={listaTemplates}
        onConcluido={onTemplateAtualizado}
      />
    </Box>
  );
}
